import { effect, inject, Injectable, signal, Signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CountryLanguageCodes } from '@nx-customer-apps/shared/enums';
import { WindowService } from '@nx-customer-apps/shared/services';
import { environment } from 'apps/planning-tools-config/src/environments/environment';
import { LanguageCountryDictionary } from '../consts';

@Injectable({
    providedIn: 'root'
})
export class LanguageSwitcherService {
    private translateService = inject(TranslateService);
    private windowService = inject(WindowService);
    private defaultLocale = CountryLanguageCodes.enDE;
    private currentLocale = signal<CountryLanguageCodes>(this.getDefaultLocale());

    constructor() {
        effect(() => {
            const locale = this.currentLocale();
            this.setStoredLocale(locale);
            this.useTranslation(locale);
        });
    }

    public getCurrentLocale(): Signal<CountryLanguageCodes> {
        return this.currentLocale.asReadonly();
    }

    public getCountryCode(): string {
        return this.getSplitLocale()[1];
    }

    public getLanguageCode(): string {
        return this.getSplitLocale()[0];
    }

    public changeLocale(locale: CountryLanguageCodes): void {
        this.currentLocale.set(locale);
    }

    private useTranslation(locale: CountryLanguageCodes): void {
        this.translateService.use(locale);
    }

    private getSplitLocale(): string[] {
        return this.currentLocale().split('-');
    }

    private setStoredLocale(locale: CountryLanguageCodes): void {
        this.windowService.localStorage.setItem(environment.storageKeys.locale, locale as CountryLanguageCodes);
    }

    private getStoredLocale(): CountryLanguageCodes {
        return (this.windowService.localStorage.getItem(environment.storageKeys.locale) as CountryLanguageCodes) || this.defaultLocale;
    }

    public getDefaultLocale(): CountryLanguageCodes {
        const supportedBrowserLocale = this.getBrowserSupportedLocale();
        const storedLocale = this.getStoredLocale();

        if (!supportedBrowserLocale) {
            if (!storedLocale) {
                return this.defaultLocale;
            }

            return storedLocale;
        }

        return supportedBrowserLocale;
    }

    private getBrowserSupportedLocale(): CountryLanguageCodes | undefined {
        const browserLang = this.translateService.getBrowserLang();

        if (!browserLang) {
            return undefined;
        }

        const locale = this.getLocaleByPrefix(browserLang);

        return locale && this.isLocaleSupported(locale) ? locale : undefined;
    }

    private getLocaleByPrefix(prefix: string): CountryLanguageCodes | undefined {
        const key = Object.keys(CountryLanguageCodes).find(key => key.toLowerCase().startsWith(prefix.toLowerCase()));

        return key ? CountryLanguageCodes[key as keyof typeof CountryLanguageCodes] : undefined;
    }

    private isLocaleSupported(locale: CountryLanguageCodes): boolean {
        return locale in LanguageCountryDictionary;
    }
}
